import { buildEnvelope } from './buildEnvelope';
import type { Event, EventPayload } from './types';

/**
 * Track an analytics event to both Amplitude (via GustoAnalytics) and Datadog RUM.
 *
 * This is the main entry point for all analytics tracking in GWS-Flows.
 * Events are sent with an `EventEnvelope` structure compatible with ZenPayroll's
 * analytics schema, enabling cross-platform event analysis.
 *
 * @param event - The event to track (`PageViewedEvent`, `ElementClickedEvent`, etc.).
 * @param target - Optional DOM element that triggered the event.
 */
export function trackEvent<T extends EventPayload>(event: Event<T>, target?: Element | null): void {
  const envelope = buildEnvelope(event, target);

  // Send to Amplitude/Snowplow via GustoAnalytics
  // CRITICAL: Pass sessionId and userId at TOP LEVEL (not in data)
  // see gusto_analytics.ts: `let userId = prop.userId || this.config.userId;
  // and `prop.sessionId || this.identifierService.getSessionId()`
  window.GustoAnalytics?.track({
    eventCategory: event.eventCategory,
    eventName: event.eventName,
    sessionId: envelope.session_id,
    // userId is required for Gusto Analytics validation and Amplitude
    userId: envelope.user.user_id,
    data: { ...envelope },
  });

  // Also send to Datadog RUM (supplementary — lazy import keeps SDK out of the main bundle).
  if (event.rumActionName) {
    void import('@datadog/browser-rum').then(({ datadogRum }) =>
      datadogRum.addAction(event.rumActionName!, envelope),
    );
  }
}
